import {useCallback, useState} from 'react';

import type {ConditionGroup} from 'studies/typed/shared/useExperimentDesignState';

import {getProcedureRunDesignFlowchartEdgeId} from './ProcedureRunDesignFlowchartEdge';
import type {FlowchartEdgeSpec} from './ProcedureRunDesignFlowchartLayoutUtils';
import {parseProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';

export interface UseDrawnConditionGraphEdgesResult {
  /** Edges the user has drawn this edit session, pruned to endpoints that still exist. */
  drawnEdgeSpecs: Array<FlowchartEdgeSpec>;
  addDrawnEdge: (spec: FlowchartEdgeSpec) => void;
  resetDrawnEdges: () => void;
}

function edgeSpecId(spec: FlowchartEdgeSpec): string {
  return getProcedureRunDesignFlowchartEdgeId(spec.sourceNodeId, spec.targetNodeId);
}

/** Whether a node id still maps to an existing condition row and replicate. */
function isEndpointResolvable(nodeId: string, conditions: ReadonlyArray<ConditionGroup>): boolean {
  const {conditionIndex, replicateIndex} = parseProcedureRunDesignFlowchartNodeId(nodeId);
  const condition = conditions[conditionIndex];
  return condition != null && replicateIndex < condition.numReplicates;
}

/**
 * Keeps only edges whose endpoints still resolve, so a condition removed mid-edit can't leave an edge
 * pointing at a missing node.
 */
export function pruneDrawnEdgeSpecs(
  drawnEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>,
  conditions: ReadonlyArray<ConditionGroup>
): Array<FlowchartEdgeSpec> {
  return drawnEdgeSpecs.filter(
    (spec) =>
      isEndpointResolvable(spec.sourceNodeId, conditions) &&
      isEndpointResolvable(spec.targetNodeId, conditions)
  );
}

/** Appends drawn specs to derived specs, dropping any drawn spec already present in the derived set. */
export function combineEdgeSpecs(
  derivedEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>,
  drawnEdgeSpecs: ReadonlyArray<FlowchartEdgeSpec>
): Array<FlowchartEdgeSpec> {
  const seenIds = new Set(derivedEdgeSpecs.map((spec) => edgeSpecId(spec)));
  const combined = [...derivedEdgeSpecs];
  for (const spec of drawnEdgeSpecs) {
    const id = edgeSpecId(spec);
    if (!seenIds.has(id)) {
      seenIds.add(id);
      combined.push(spec);
    }
  }
  return combined;
}

/**
 * Holds the run tab's draft drawn edges in memory (parallel to draft conditions): added as the user
 * draws, pruned as conditions change, and dropped on reset. They are merged into the save payload, and
 * only persist once the user saves. Drafts are never cleared on save - they are reset only on cancel
 * or naturally on remount (a fresh mount starts with zero drafts and a refreshed design query). The
 * draft and its eventual derived twin share the same edge id, so the single merge dedupes them and the
 * edge stays continuously rendered across a save, exactly like newly added conditions.
 */
export function useDrawnConditionGraphEdges(
  conditions: ReadonlyArray<ConditionGroup>
): UseDrawnConditionGraphEdgesResult {
  const [drawnEdgeSpecs, setDrawnEdgeSpecs] = useState<Array<FlowchartEdgeSpec>>([]);

  // Reconcile against the current conditions during render (React's "adjust state on prop change"
  // pattern, preferred over an effect). Dropping edges whose endpoints no longer resolve and writing
  // the result back means a stale edge can't revive onto a shifted index-based node id, with no stale
  // frame. Reuse the existing reference when nothing changed so downstream memos don't churn.
  const reconciledEdgeSpecs = pruneDrawnEdgeSpecs(drawnEdgeSpecs, conditions);
  const nextEdgeSpecs =
    reconciledEdgeSpecs.length === drawnEdgeSpecs.length ? drawnEdgeSpecs : reconciledEdgeSpecs;
  if (nextEdgeSpecs !== drawnEdgeSpecs) {
    setDrawnEdgeSpecs(nextEdgeSpecs);
  }

  const addDrawnEdge = useCallback((spec: FlowchartEdgeSpec) => {
    setDrawnEdgeSpecs((current) => {
      const id = edgeSpecId(spec);
      return current.some((existing) => edgeSpecId(existing) === id) ? current : [...current, spec];
    });
  }, []);

  const resetDrawnEdges = useCallback(() => {
    setDrawnEdgeSpecs([]);
  }, []);

  return {drawnEdgeSpecs: nextEdgeSpecs, addDrawnEdge, resetDrawnEdges};
}
